import { Controller } from "@hotwired/stimulus"

// Small paper moments on a task card, for things that happen while you watch.
//
// When a run finishes in front of you, its tape is stamped down. A finished
// run throws a handful of paper scraps; a failed one crumples for a moment.
// A page load never plays either: only a change of tone does. The card never
// moves on its own otherwise: a 3D tilt softened its text, and a sheet you
// read should stay crisp.
//
// A live broadcast can morph the card in place or replace it outright, so the
// last tone each card showed is kept by its key for the life of the page, not
// on the element. Nothing here runs when the reader asks for reduced motion.
//
// Usage: data-controller="paper-moment" data-paper-moment-key-value="TASK-12"
// on a .task-run-card that carries data-tone.

const lastTones = new Map()
// Retain recent replacements without accumulating every run on a long timeline.
const MAX_TONES = 500
// Turbo keeps this module alive between pages, including cached restorations.
for (const event of ["turbo:before-cache", "turbo:before-render"]) {
  document.addEventListener(event, () => lastTones.clear())
}
const OPEN_TONES = new Set(["running", "waiting", "hold"])
// The family colours (globals.css --fam-*), so scraps follow the palette.
const SCRAP_COLORS = ["done", "work", "people", "mail", "bad"].map((family) => `var(--fam-${family})`)

export default class extends Controller {
  static values = { key: String }

  connect() {
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.tone = undefined
    this.beforeCache = () => this.clearMoment()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.noteTone()

    this.observer = new MutationObserver(() => this.noteTone())
    this.observer.observe(this.element, { attributes: true, attributeFilter: ["data-tone"] })
  }

  disconnect() {
    this.observer?.disconnect()
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.clearMoment()
  }

  clearMoment() {
    clearTimeout(this.cleanup)
    this.scraps?.forEach((scrap) => scrap.remove())
    this.scraps = []
    this.animations?.forEach(({ element, className, finish }) => {
      element.removeEventListener("animationend", finish)
      element.classList.remove(className)
    })
    this.animations = []
  }

  noteTone() {
    const tone = this.element.dataset.tone
    const key = this.keyValue || this.element.id
    const before = key ? lastTones.get(key) : this.tone
    this.tone = tone
    if (key) {
      lastTones.delete(key)
      lastTones.set(key, tone)
      if (lastTones.size > MAX_TONES) lastTones.delete(lastTones.keys().next().value)
    }
    if (before !== tone) this.clearMoment()

    if (this.motion.matches || !before || before === tone || !OPEN_TONES.has(before)) return
    if (tone === "success") this.celebrate()
    else if (tone === "error") this.crumple()
    else return
    this.cleanup = setTimeout(() => this.clearMoment(), 1100)
  }

  celebrate() {
    this.stamp()
    const marks = this.element.querySelector(".task-run-card-marks")
    if (!marks) return

    this.scraps = SCRAP_COLORS.concat(SCRAP_COLORS).map((color, index) => {
      const scrap = document.createElement("span")
      const angle = (Math.PI * (index + 0.5)) / 10 + Math.PI
      const reach = 30 + Math.random() * 26
      scrap.className = "paper-scrap"
      scrap.setAttribute("aria-hidden", "true")
      scrap.style.setProperty("--scrap-color", color)
      scrap.style.setProperty("--scrap-x", `${Math.cos(angle) * reach}px`)
      scrap.style.setProperty("--scrap-y", `${Math.sin(angle) * reach - 6}px`)
      scrap.style.setProperty("--scrap-turn", `${Math.round(Math.random() * 540 - 270)}deg`)
      marks.appendChild(scrap)
      return scrap
    })
  }

  crumple() {
    this.stamp()
    this.replay(this.element, "paper-crumple")
  }

  stamp() {
    // The card's state mark (the details panel's circle); older cards wore tape.
    const tape = this.element.querySelector(".task-run-card-mark, .task-run-card-state")
    if (tape) this.replay(tape, "paper-stamp")
  }

  // Restart a one-shot animation class even if it is already on the element.
  replay(element, className) {
    element.classList.remove(className)
    void element.offsetWidth
    element.classList.add(className)
    const finish = (event) => {
      if (event.target !== element || event.animationName !== className) return
      element.classList.remove(className)
      element.removeEventListener("animationend", finish)
    }
    this.animations.push({ element, className, finish })
    element.addEventListener("animationend", finish)
  }
}
